import type {
  BlockAuditEntry,
  BlockStatusResponse,
  BlockUserRequest,
  UnblockUserRequest,
} from "@/domain/blocking";
import { apiClientJSON } from "../apiClient";

const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

const transformKeys = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map(transformKeys) as T;
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const camelKey = toCamelCase(key);
        acc[camelKey] = transformKeys((obj as Record<string, unknown>)[key]);
        return acc;
      },
      {} as Record<string, unknown>
    ) as T;
  }
  return obj;
};

export const getBlockStatus = async (
  userId: string
): Promise<BlockStatusResponse> => {
  return apiClientJSON
    .get(`/account-service/dashboard/users/${userId}/block-status`)
    .then((r) => transformKeys(r.data));
};

export const blockUser = async (
  userId: string,
  request: BlockUserRequest
): Promise<BlockStatusResponse> => {
  return apiClientJSON
    .post(`/account-service/dashboard/users/${userId}/block`, {
      blockReason: request.reason,
      blockMessage: request.message,
      internalNotes: request.internalNotes,
    })
    .then((r) => transformKeys(r.data));
};

export const unblockUser = async (
  userId: string,
  request: UnblockUserRequest
): Promise<BlockStatusResponse> => {
  return apiClientJSON
    .post(`/account-service/dashboard/users/${userId}/unblock`, {
      resolutionNotes: request.resolutionNotes,
    })
    .then((r) => transformKeys(r.data));
};

export const updateBlockMessage = async (
  userId: string,
  message: string
): Promise<BlockStatusResponse> => {
  return apiClientJSON
    .put(`/account-service/dashboard/users/${userId}/block-message`, {
      blockMessage: message,
    })
    .then((r) => transformKeys(r.data));
};

export const updateBlockNotes = async (
  userId: string,
  internalNotes: string
): Promise<BlockStatusResponse> => {
  return apiClientJSON
    .put(`/account-service/dashboard/users/${userId}/block-notes`, {
      internalNotes: internalNotes,
    })
    .then((r) => transformKeys(r.data));
};

export const getBlockHistory = async (
  userId: string
): Promise<BlockAuditEntry[]> => {
  return apiClientJSON
    .get(`/account-service/dashboard/users/${userId}/block-history`)
    .then((r) => transformKeys(r.data));
};
